import React from 'react'
import { sanitize } from 'dompurify'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import { ActionElementType } from 'Components/SharedComponents/Surveys/constants'
import {
  Description,
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import { UserActions } from 'Components/SharedComponents/Surveys/UserActions/UserActions'
import {
  ButtonAction,
  CustomAction,
} from 'Components/SharedComponents/Surveys/types'
import { HandleAnyAction, OrgLeaderInvite } from '../../types'
import { NeedsAssessmentWizardStep } from '../../constants'
import { sendStepTransitionEvent } from '../../utils'

type InviteYourOrgLeaderProps = {
  onAnyAction: HandleAnyAction
  onInviteOrgLeader: () => void
  orgLeaderInvite: OrgLeaderInvite
  setOrgLeaderInvite: (args: OrgLeaderInvite) => void
  userJourneyLength: number
  userPrimaryFunction: UserSurveyPrimaryFunction | undefined
  userRole?: UserSurveyRole | undefined
}

export const InviteYourOrgLeader = ({
  onAnyAction,
  onInviteOrgLeader,
  orgLeaderInvite,
  setOrgLeaderInvite,
  userJourneyLength,
  userPrimaryFunction,
  userRole,
}: InviteYourOrgLeaderProps) => {
  const { nonprofitOrgConfig, userConfig } = useAppState()
  const hasAlreadyInvitedEmail = orgLeaderInvite.emailsInvited.includes(
    orgLeaderInvite.email
  )

  const isInviteButtonDisabled =
    Object.values(orgLeaderInvite).some((value) => !value) ||
    !orgLeaderInvite.email.includes('@') ||
    hasAlreadyInvitedEmail

  const userActions: CustomAction[] = [
    {
      callback: (...args: unknown[]) =>
        setOrgLeaderInvite({
          ...orgLeaderInvite,
          firstName: sanitize(args[0] as string),
        }),
      label: 'First Name',
      type: ActionElementType.TEXT_FIELD,
      value: orgLeaderInvite.firstName,
    },
    {
      callback: (...args: unknown[]) =>
        setOrgLeaderInvite({
          ...orgLeaderInvite,
          lastName: sanitize(args[0] as string),
        }),
      label: 'Last Name',
      type: ActionElementType.TEXT_FIELD,
      value: orgLeaderInvite.lastName,
    },
    {
      callback: (...args: unknown[]) =>
        setOrgLeaderInvite({
          ...orgLeaderInvite,
          email: sanitize(args[0] as string),
        }),
      label: 'Add Email',
      type: ActionElementType.TEXT_FIELD,
      value: orgLeaderInvite.email,
    },
  ]

  const nextStep = NeedsAssessmentWizardStep.SELECT_YOUR_PRIORITIES
  const nextActions = [
    {
      label: 'Skip',
      path: nextStep,
      type: ActionElementType.BUTTON,
      variant: 'secondary',
      callback: () => {
        setOrgLeaderInvite({
          email: '',
          firstName: '',
          lastName: '',
          emailsInvited: orgLeaderInvite.emailsInvited,
        })
        sendStepTransitionEvent({
          currentStep: NeedsAssessmentWizardStep.INVITE_YOUR_ORG_LEADER,
          nextStep,
          userId: userConfig.id,
          nonprofitOrgId: nonprofitOrgConfig.orgId,
          funderOrgId: nonprofitOrgConfig.funderOrgId,
          userEmail: userConfig.email,
          userPrimaryFunction,
          userJourneyLength,
          userRole,
        })
      },
    },
    {
      callback: onInviteOrgLeader,
      disabled: isInviteButtonDisabled,
      label: hasAlreadyInvitedEmail ? 'Already Invited' : 'Invite and Continue',
      path: nextStep,
      type: ActionElementType.BUTTON,
    },
  ] as ButtonAction[]

  return (
    <StepContainer>
      <Title>Invite your Org Leader or Executive Director</Title>
      <Description>
        Please invite the your Org Leader or Executive Director. We are looking
        for the best person to set your organization&apos;s priorities and
        assign tasks. <br />
        <br />
        Next, you&apos;ll share your individual focus areas.
      </Description>
      <UserActions onAnyAction={onAnyAction} userActions={userActions} />
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
